import * as RadixDialog from '@radix-ui/react-dialog';
import {
  ArrowClockwise,
  DownloadSimple,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  TrashSimple,
  X,
} from '@phosphor-icons/react';
import { useState } from 'react';
import type { Attachment } from '@/data/db';
import { IconButton } from '@/components/primitives/icon-button';
import { TooltipProvider } from '@/components/primitives/tooltip';
import { formatBytes } from '@/data/storage';
import { useBlobUrl } from './use-attachments';

const ZOOM_STEPS = [0.5, 0.75, 1, 1.5, 2, 3] as const;

interface ImageViewerProps {
  attachment: Attachment | null;
  onOpenChange: (open: boolean) => void;
  onDelete?: (attachment: Attachment) => void;
}

/** 圖片檢視器：放大、縮小、旋轉、下載與刪除（spec 16.2）。 */
export function ImageViewer({ attachment, onOpenChange, onDelete }: ImageViewerProps) {
  const [zoomIndex, setZoomIndex] = useState(2);
  const [rotation, setRotation] = useState(0);
  const url = useBlobUrl(attachment?.blob ?? null);

  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;

  function download() {
    if (!url || !attachment) return;
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = attachment.fileName;
    anchor.click();
  }

  return (
    <RadixDialog.Root
      open={attachment !== null}
      onOpenChange={(next) => {
        if (!next) {
          setZoomIndex(2);
          setRotation(0);
        }
        onOpenChange(next);
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-[color-mix(in_srgb,var(--text)_60%,transparent)]" />
        <RadixDialog.Content className="fixed inset-6 z-50 flex flex-col overflow-hidden rounded-[var(--radius-shell)] bg-surface-raised shadow-[var(--shadow-lifted)]">
          <TooltipProvider delayDuration={280}>
            <header className="flex items-center gap-3 border-b border-border px-5 py-3">
              <div className="min-w-0 flex-1">
                <RadixDialog.Title className="truncate text-[0.9375rem] font-medium">
                  {attachment?.fileName ?? ''}
                </RadixDialog.Title>
                <RadixDialog.Description className="tabular text-[0.75rem] text-text-muted">
                  {attachment
                    ? `${attachment.width} × ${attachment.height}・${formatBytes(attachment.size)}` +
                      `（原始 ${formatBytes(attachment.originalSize)}）`
                    : ''}
                </RadixDialog.Description>
              </div>

              <IconButton
                label="縮小"
                icon={<MagnifyingGlassMinus size={17} />}
                disabled={zoomIndex === 0}
                onClick={() => setZoomIndex((current) => Math.max(0, current - 1))}
              />
              <span className="tabular w-12 text-center text-[0.8125rem] text-text-muted">
                {Math.round(zoom * 100)}%
              </span>
              <IconButton
                label="放大"
                icon={<MagnifyingGlassPlus size={17} />}
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
                onClick={() =>
                  setZoomIndex((current) => Math.min(ZOOM_STEPS.length - 1, current + 1))
                }
              />
              <IconButton
                label="旋轉 90 度"
                icon={<ArrowClockwise size={17} />}
                onClick={() => setRotation((current) => (current + 90) % 360)}
              />
              <IconButton label="下載" icon={<DownloadSimple size={17} />} onClick={download} />
              {onDelete && attachment && (
                <IconButton
                  label="刪除這張圖片"
                  icon={<TrashSimple size={17} />}
                  onClick={() => onDelete(attachment)}
                />
              )}
              <RadixDialog.Close asChild>
                <IconButton label="關閉" icon={<X size={18} />} />
              </RadixDialog.Close>
            </header>

            <div className="grid min-h-0 flex-1 place-items-center overflow-auto bg-surface-sunken p-6">
              {url && (
                <img
                  src={url}
                  alt={attachment?.fileName ?? ''}
                  style={{
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transformOrigin: 'center',
                  }}
                  className="max-h-full max-w-full transition-transform duration-300 ease-[var(--ease-gentle)]"
                />
              )}
            </div>
          </TooltipProvider>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
